import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ImageUploader } from './upload/ImageUploader';
import { OutputControls } from './output/OutputControls';
import { ImagePreview } from './preview/ImagePreview';
import { Download, RefreshCw, HelpCircle, Image as ImageIcon, Sliders, Layers, Scissors, X } from 'lucide-react';
import { CropInterface } from './crop/CropInterface';
import { ImagePreset } from './resize/PresetSelector';
import { INITIAL_ADJUSTMENTS, ImageAdjustmentsState } from './edit/ImageAdjustments';
import { INITIAL_WATERMARK, WatermarkState } from './edit/WatermarkEditor';
import { BeforeAfterSlider } from './preview/BeforeAfterSlider';
import { BatchUploader } from './batch/BatchUploader';
import { CreatorBacklink } from './ui/CreatorBacklink';
import { ThemeToggle } from './ui/ThemeToggle';
import { BlivixLogo } from './ui/BlivixLogo';
import { LandingHub } from './home/LandingHub';
import { VideoEditor } from './video/VideoEditor';
import { StandaloneBgRemover } from './bg/StandaloneBgRemover';
import { AccordionControlGroup } from './edit/AccordionControlGroup';
import { processImage } from '../utils/imageProcessing';
import { getImageDimensions } from '../utils/imageUtils';
import type { ResizeMethod } from '../types/resize';

interface Dimensions {
  width: number;
  height: number;
}

type AppRoute = 'home' | 'image' | 'video' | 'bg' | 'batch';

function getRouteFromUrl(): AppRoute {
  const path = window.location.pathname.toLowerCase();
  if (path.includes('/video')) return 'video';
  if (path.includes('/bg') || path.includes('/background')) return 'bg';
  if (path.includes('/batch')) return 'batch';
  if (path.includes('/image')) return 'image';
  return 'home';
}

export function ImageEditor() {
  const [appRoute, setAppRoute] = useState<AppRoute>(getRouteFromUrl);
  const [originalImage, setOriginalImage] = useState<File | null>(null);
  const [isMobileControlsOpen, setIsMobileControlsOpen] = useState<boolean>(false);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [showCropInterface, setShowCropInterface] = useState(false);
  const [cropAspectRatio, setCropAspectRatio] = useState<number | undefined>(undefined);
  const [resizeMethod, setResizeMethod] = useState<ResizeMethod>('dimension');
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [outputFormat, setOutputFormat] = useState('image/jpeg');
  const [outputQuality, setOutputQuality] = useState(90);
  const [lastDimensions, setLastDimensions] = useState<Dimensions | undefined>(undefined);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [adjustments, setAdjustments] = useState<ImageAdjustmentsState>(INITIAL_ADJUSTMENTS);
  const [watermark, setWatermark] = useState<WatermarkState>(INITIAL_WATERMARK);
  const [showComparison, setShowComparison] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);

  // Sync browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setAppRoute(getRouteFromUrl());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToRoute = (route: AppRoute) => {
    setAppRoute(route);
    if (route === 'home') {
      setOriginalImage(null);
      setProcessedImage(null);
      setActivePresetId(null);
      setShowCropInterface(false);
    }
    let path = '/';
    let title = 'Blivix Studio Pro | 100% Local Creative Suite';

    if (route === 'image') {
      path = '/image-editor';
      title = 'Blivix Image Studio | Free Local Photo Resizer & Cropper';
    } else if (route === 'video') {
      path = '/video-editor';
      title = 'Blivix Video Studio Pro | Multi-Track Client-Side Editor';
    } else if (route === 'bg') {
      path = '/bg-remover';
      title = 'Blivix AI Background Remover | 100% Free Local Background Stripper';
    } else if (route === 'batch') {
      path = '/batch-resizer';
      title = 'Blivix Batch Resizer | Bulk Image Compression & ZIP Download';
    }

    window.history.pushState(null, '', path);
    document.title = title;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Manage original image object URL safely
  useEffect(() => {
    if (!originalImage) {
      setOriginalUrl(null);
      return;
    }
    const url = URL.createObjectURL(originalImage);
    setOriginalUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [originalImage]);

  // Global Clipboard Paste Listener (Ctrl+V / Cmd+V)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;

      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
            break;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  // Re-process image whenever adjustments or watermark change
  useEffect(() => {
    if (!originalImage) return;

    const applyLiveEffects = async () => {
      try {
        setIsProcessing(true);
        const dims = lastDimensions || (await getImageDimensions(originalImage));

        const result = await processImage(originalImage, {
          resize: {
            width: dims.width,
            height: dims.height,
            maintainAspectRatio: !activePresetId,
            fitMode: 'crop',
          },
          adjustments,
          watermark,
          output: {
            format: outputFormat,
            quality: outputQuality,
          },
        });

        setProcessedImage(result);
      } catch (err) {
        console.error('Failed to apply adjustments:', err);
      } finally {
        setIsProcessing(false);
      }
    };

    applyLiveEffects();
  }, [adjustments, watermark, outputFormat, outputQuality]);

  const handleImageUpload = (file: File) => {
    setOriginalImage(file);
    // NO crop popup window - load straight into the studio!
    setAdjustments(INITIAL_ADJUSTMENTS);
    setWatermark(INITIAL_WATERMARK);
    setActivePresetId(null);
    setCropAspectRatio(undefined);
  };

  const handleCrop = useCallback(async (croppedImageDataUrl: string) => {
    try {
      const response = await fetch(croppedImageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], originalImage?.name || 'cropped-image.png', {
        type: 'image/png',
      });
      setOriginalImage(file);
      setProcessedImage(croppedImageDataUrl);
      setShowCropInterface(false);
    } catch (error) {
      console.error('Error processing cropped image:', error);
    }
  }, [originalImage?.name]);

  const handlePresetSelect = (preset: ImagePreset) => {
    setActivePresetId(preset.id);
    const newDims = { width: preset.width, height: preset.height };
    setLastDimensions(newDims);
    setResizeMethod('dimension');

    if (originalImage) {
      processImage(originalImage, {
        resize: { width: preset.width, height: preset.height, maintainAspectRatio: false, fitMode: 'crop' },
        adjustments,
        watermark,
        output: { format: outputFormat, quality: outputQuality },
      }).then((result) => setProcessedImage(result));
    }
  };

  const handleAdjustPresetCrop = (preset: ImagePreset) => {
    setActivePresetId(preset.id);
    setCropAspectRatio(preset.width / preset.height);
    setShowCropInterface(true);
  };

  const handleDownload = () => {
    if (!processedImage) return;
    const link = document.createElement('a');
    const ext = outputFormat.split('/')[1] || 'jpeg';
    link.download = `blivix-studio-${Date.now()}.${ext}`;
    link.href = processedImage;
    link.click();
  };

  const handleRefresh = () => {
    setOriginalImage(null);
    setProcessedImage(null);
    setActivePresetId(null);
    setLastDimensions(undefined);
    setAdjustments(INITIAL_ADJUSTMENTS);
    setWatermark(INITIAL_WATERMARK);
    setShowCropInterface(false);
    if (appRoute !== 'home') {
      navigateToRoute('home');
    }
  };

  const handleProcess = (dataUrl: string) => {
    setProcessedImage(dataUrl);
    const img = new Image();
    img.onload = () => {
      setLastDimensions({ width: img.width, height: img.height });
    };
    img.src = dataUrl;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50/20 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-slate-900 dark:text-slate-100 font-sans antialiased transition-colors duration-300 flex flex-col justify-between">
      {/* Top Navbar Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800/80 px-4 sm:px-8 py-3 transition-colors">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <BlivixLogo onClick={() => navigateToRoute('home')} />

          {/* Navigation Controls */}
          <div className="flex items-center gap-3">
            {appRoute !== 'home' || originalImage !== null ? (
              <button
                type="button"
                onClick={() => navigateToRoute('home')}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-all flex items-center gap-1.5 shadow-sm hover:scale-105"
              >
                <span>🏠</span>
                <span>Back to Main Hub</span>
              </button>
            ) : null}

            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Main Workspace Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-6 py-4">
        {appRoute === 'home' && !originalImage ? (
          <LandingHub onNavigate={(r) => navigateToRoute(r)} />
        ) : appRoute === 'video' ? (
          <VideoEditor />
        ) : appRoute === 'bg' ? (
          <StandaloneBgRemover />
        ) : appRoute === 'batch' ? (
          <BatchUploader />
        ) : (
          <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 dark:border-slate-800">
            {!originalImage ? (
              <div className="p-6 sm:p-10 max-w-4xl mx-auto">
                <ImageUploader onImageUpload={handleImageUpload} />
              </div>
            ) : (
              <>
                {/* Compact Sticky Header */}
                <div className="sticky top-0 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-4 py-2 rounded-t-3xl flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate max-w-[180px] sm:max-w-[280px]">
                      📁 {originalImage.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Mobile Trigger Button for Accordion Drawer */}
                    <button
                      type="button"
                      onClick={() => setIsMobileControlsOpen(true)}
                      className="xl:hidden text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-3 py-1.5 rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
                    >
                      <Sliders className="w-3.5 h-3.5" />
                      <span>Studio Controls</span>
                    </button>

                    <button
                      onClick={() => setShowHelp(!showHelp)}
                      className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 flex items-center gap-1 font-medium"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span className="hidden sm:inline">Help</span>
                    </button>
                    <button
                      onClick={handleRefresh}
                      className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 flex items-center gap-1 font-medium"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="hidden sm:inline">Start Over</span>
                    </button>
                  </div>
                </div>

                <div className="p-3 sm:p-5 space-y-4">
                  {showHelp && (
                    <div className="bg-indigo-50 dark:bg-indigo-950/40 p-3 rounded-xl border border-indigo-100 dark:border-indigo-900 text-xs text-indigo-900 dark:text-indigo-300 space-y-1">
                      <h3 className="font-semibold">Quick Guide</h3>
                      <p>• <strong>Accordion Controls</strong>: Select any section on the right to edit dimensions, crop, format, color filters, background removal, or watermarking.</p>
                      <p>• <strong>Live Canvas</strong>: Preview processed output in real-time with split comparison view.</p>
                    </div>
                  )}

                  {showCropInterface ? (
                    <CropInterface
                      image={originalImage}
                      onCrop={handleCrop}
                      onBack={() => setShowCropInterface(false)}
                      initialAspectRatio={cropAspectRatio}
                    />
                  ) : (
                    /* Widescreen 2-Column Desktop Grid Layout */
                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
                      {/* LEFT COLUMN STAGE: Canvas Preview (xl:col-span-7 2xl:col-span-8) */}
                      <div className="xl:col-span-7 2xl:col-span-8 space-y-4 xl:sticky xl:top-14">
                        <div className="bg-slate-50 dark:bg-slate-950/60 p-3 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-3">
                          <div className="flex justify-between items-center">
                            <h3 className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5 uppercase tracking-wider">
                              <span>🎨</span>
                              <span>Output Canvas Preview</span>
                            </h3>
                            {originalUrl && processedImage && (
                              <button
                                type="button"
                                onClick={() => setShowComparison(!showComparison)}
                                className="text-[11px] text-indigo-600 dark:text-indigo-400 font-bold hover:underline flex items-center gap-1.5 bg-white dark:bg-slate-900 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm"
                              >
                                <Layers className="w-3.5 h-3.5" />
                                {showComparison ? 'Standard View' : 'Split Comparison'}
                              </button>
                            )}
                          </div>

                          {/* Preview Canvas Stage */}
                          {showComparison && originalUrl && processedImage ? (
                            <BeforeAfterSlider
                              originalUrl={originalUrl}
                              processedUrl={processedImage}
                            />
                          ) : (
                            <ImagePreview
                              original={originalImage}
                              processed={processedImage}
                            />
                          )}

                          {/* Download Action Bar */}
                          {processedImage && (
                            <div className="flex flex-col sm:flex-row justify-center pt-1 gap-2.5">
                              <button
                                ref={downloadButtonRef}
                                type="button"
                                onClick={handleDownload}
                                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-700 hover:to-pink-600 text-white font-bold text-xs rounded-xl transition-all shadow-lg shadow-indigo-600/30 hover:scale-[1.01]"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download Processed Image
                              </button>
                              <button
                                type="button"
                                onClick={handleRefresh}
                                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-semibold text-xs rounded-xl transition-all"
                              >
                                <RefreshCw className="w-3.5 h-3.5 mr-2" />
                                Start Over
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* RIGHT COLUMN DOCK: All Studio Control Sections Grouped in Single-Expanded Accordion */}
                      <div className="hidden xl:block xl:col-span-5 2xl:col-span-4">
                        <AccordionControlGroup
                          originalImage={originalImage}
                          processedImage={processedImage}
                          resizeMethod={resizeMethod}
                          setResizeMethod={setResizeMethod}
                          outputFormat={outputFormat}
                          outputQuality={outputQuality}
                          setOutputFormat={setOutputFormat}
                          setOutputQuality={setOutputQuality}
                          lastDimensions={lastDimensions}
                          activePresetId={activePresetId}
                          adjustments={adjustments}
                          setAdjustments={setAdjustments}
                          watermark={watermark}
                          setWatermark={setWatermark}
                          setShowCropInterface={setShowCropInterface}
                          handleProcess={handleProcess}
                          setIsProcessing={setIsProcessing}
                          handlePresetSelect={handlePresetSelect}
                          handleAdjustPresetCrop={handleAdjustPresetCrop}
                          setProcessedImage={setProcessedImage}
                        />
                      </div>

                      {/* MOBILE / TABLET FLOATING SEMI-TRANSPARENT SIDEBAR DRAWER */}
                      {isMobileControlsOpen && (
                        <div className="xl:hidden fixed inset-0 z-50 flex justify-end bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
                          <div className="w-full sm:w-[420px] bg-slate-900 border-l border-slate-800 p-5 overflow-y-auto h-full space-y-4 shadow-2xl">
                            <div className="flex justify-between items-center pb-3 border-b border-slate-800">
                              <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                                <Sliders className="w-4 h-4 text-indigo-400" />
                                <span>Studio Editing Accordion</span>
                              </h3>
                              <button
                                type="button"
                                onClick={() => setIsMobileControlsOpen(false)}
                                className="p-1.5 text-slate-400 hover:text-white bg-slate-800 rounded-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>

                            <AccordionControlGroup
                              originalImage={originalImage}
                              processedImage={processedImage}
                              resizeMethod={resizeMethod}
                              setResizeMethod={setResizeMethod}
                              outputFormat={outputFormat}
                              outputQuality={outputQuality}
                              setOutputFormat={setOutputFormat}
                              setOutputQuality={setOutputQuality}
                              lastDimensions={lastDimensions}
                              activePresetId={activePresetId}
                              adjustments={adjustments}
                              setAdjustments={setAdjustments}
                              watermark={watermark}
                              setWatermark={setWatermark}
                              setShowCropInterface={setShowCropInterface}
                              handleProcess={handleProcess}
                              setIsProcessing={setIsProcessing}
                              handlePresetSelect={handlePresetSelect}
                              handleAdjustPresetCrop={handleAdjustPresetCrop}
                              setProcessedImage={setProcessedImage}
                            />

                            <button
                              type="button"
                              onClick={() => setIsMobileControlsOpen(false)}
                              className="w-full py-3 bg-indigo-600 text-white font-bold text-xs rounded-xl shadow-lg mt-4"
                            >
                              Apply & Return to Canvas
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer Info */}
        <footer className="mt-8 text-center text-xs text-slate-500 dark:text-slate-400 space-y-2 py-4">
          <CreatorBacklink variant="footer" />
        </footer>
      </main>
    </div>
  );
}